import { api } from "../client";
import { API_BASE_URL } from "../../utils/constants";

/**
 * Call dynesourcerapi.calculate.get_mortgage_products_details
 *
 * @param {Object} payload - {
 *   property_value,
 *   mortgage_amount,
 *   estimated_rent,
 *   mortgage_term_years,
 *   filters: { ... }
 * }
 *
 * @returns {Promise<{
 *   filtersApplied: Array,
 *   count: number,
 *   products: Array,
 *   scheduled_products?: Array
 * }>}
 */
export async function calculateMortgageProducts(payload) {
  const url = `${API_BASE_URL}/api/method/dynesourcerapi.calculate.get_mortgage_products_details`;

  const res = await api.post(url, payload, { _guestFallback: true });
  const data = res?.data;

  if (!data || typeof data !== "object") {
    throw new Error("Unexpected API response: missing data");
  }

  const msg = data.message;
  if (!msg || typeof msg !== "object") {
    throw new Error("Unexpected API response: missing message");
  }

  if (msg.success === false) {
    const err = new Error(msg.error || "Failed to fetch products");
    err.details = msg;
    throw err;
  }

  // Your backend sometimes returns arrays under different keys
  const allProductsArray = Array.isArray(msg.data)
    ? msg.data
    : Array.isArray(msg.results)
      ? msg.results
      : Array.isArray(msg.products)
        ? msg.products
        : [];

  // Separate products based on product_schedule field
  // product_schedule === 1 means scheduled, product_schedule === 0 means live
  const productsArray = [];
  const scheduledArray = [];

  allProductsArray.forEach((product) => {
    // Check if product_schedule is 1 (scheduled) or if it's explicitly in scheduled_products
    if (product?.product_schedule === 1 || product?.product_schedule === "1") {
      scheduledArray.push(product);
    } else {
      // Default to live products (product_schedule === 0 or not set)
      productsArray.push(product);
    }
  });

  // Also check for separate scheduled_products array in response (backward compatibility)
  const explicitScheduled = Array.isArray(msg.scheduled_products)
    ? msg.scheduled_products
    : Array.isArray(msg.scheduled)
      ? msg.scheduled
      : [];

  // Merge explicit scheduled products if they exist
  if (explicitScheduled.length > 0) {
    scheduledArray.push(...explicitScheduled);
  }

  // ✅ ANALYTICS TRACKING: Store sourcing run data in localStorage
  try {
    const filterData = payload.filters || {};
    console.log("📊 Tracking sourcing analytics:", {
      hasFilters: Object.keys(filterData).length > 0,
      filterKeys: Object.keys(filterData),
      filters: filterData,
      resultsCount: allProductsArray.length,
    });

    // Include repayment type and mortgage term in filters if available
    const enhancedFilters = { ...filterData };
    
    // Add repayment type if available (from frontend state, not API)
    // Note: repaymentType is a frontend calculation parameter, not a backend filter
    // We'll track it separately if it's passed in the payload
    if (payload.repaymentType) {
      enhancedFilters.repayment_type = payload.repaymentType;
    }
    
    // Add mortgage term to filters if available
    if (payload.mortgage_term_years) {
      enhancedFilters.mortgage_term_years = payload.mortgage_term_years;
    }
    
    trackSourcingAnalytics({
      filters: enhancedFilters,
      property_value: payload.property_value,
      mortgage_amount: payload.mortgage_amount,
      estimated_rent: payload.estimated_rent,
      mortgage_term_years: payload.mortgage_term_years,
      results_count: allProductsArray.length,
      live_products_count: productsArray.length,
      scheduled_products_count: scheduledArray.length,
      filters_applied: msg.filters_applied || [],
    });
  } catch (trackingError) {
    // Don't let tracking errors affect the user experience
    console.error("Analytics tracking failed:", trackingError);
  }

  return {
    filtersApplied: msg.filters_applied || [],
    count:
      typeof msg.count === "number" ? msg.count : (allProductsArray?.length || 0),
    products: productsArray,
    scheduled_products: scheduledArray,
  };
}

/**
 * Track sourcing analytics in localStorage (frontend-only tracking)
 * Stores filter usage and search results for admin analytics dashboard
 */
function trackSourcingAnalytics(analyticsData) {
  try {
    const event = {
      event_type: "sourcing_run",
      filters: analyticsData.filters,
      search_params: {
        property_value: analyticsData.property_value,
        mortgage_amount: analyticsData.mortgage_amount,
        estimated_rent: analyticsData.estimated_rent,
        mortgage_term_years: analyticsData.mortgage_term_years,
      },
      results: {
        total_count: analyticsData.results_count,
        live_count: analyticsData.live_products_count,
        scheduled_count: analyticsData.scheduled_products_count,
      },
      filters_applied: analyticsData.filters_applied,
      timestamp: new Date().toISOString(),
    };

    console.log("💾 Storing analytics event:", {
      filterCount: Object.keys(event.filters).length,
      filters: event.filters,
    });

    // Get existing analytics data
    const existingData = localStorage.getItem("ds_sourcing_analytics");
    const analytics = existingData ? JSON.parse(existingData) : { events: [] };

    // Add new event
    analytics.events.push(event);

    // Keep only last 1000 events to avoid localStorage limits
    if (analytics.events.length > 1000) {
      analytics.events = analytics.events.slice(-1000);
    }

    // Save back to localStorage
    localStorage.setItem("ds_sourcing_analytics", JSON.stringify(analytics));

    console.log("✅ Analytics stored. Total events:", analytics.events.length);
  } catch (error) {
    // Silently fail - tracking errors should not affect functionality
    console.error("Failed to store analytics:", error);
  }
}

/**
 * Get sourcing analytics data from localStorage
 * Used by admin panel to display analytics
 */
export function getSourcingAnalytics(dateRange = null) {
  try {
    const data = localStorage.getItem("ds_sourcing_analytics");
    if (!data) {
      return { events: [], summary: getEmptySummary() };
    }

    const analytics = JSON.parse(data);
    let events = analytics.events || [];

    // Filter by date range if provided
    if (dateRange && dateRange.from && dateRange.to) {
      const fromDate = new Date(dateRange.from);
      const toDate = new Date(dateRange.to);
      events = events.filter(e => {
        const eventDate = new Date(e.timestamp);
        return eventDate >= fromDate && eventDate <= toDate;
      });
    }

    // Generate summary statistics
    const summary = generateSummary(events);

    return { events, summary };
  } catch (error) {
    console.error("Failed to retrieve analytics:", error);
    return { events: [], summary: getEmptySummary() };
  }
}

/**
 * Generate summary statistics from events
 */
function generateSummary(events) {
  if (!events || events.length === 0) {
    return getEmptySummary();
  }

  const filterUsage = {};
  let totalResults = 0;
  let totalSearches = events.length;

  events.forEach(event => {
    // Count filter usage
    if (event.filters) {
      Object.entries(event.filters).forEach(([key, value]) => {
        if (!filterUsage[key]) {
          filterUsage[key] = {};
        }
        const valueStr = String(value);
        filterUsage[key][valueStr] = (filterUsage[key][valueStr] || 0) + 1;
      });
    }

    // Sum results
    if (event.results && event.results.total_count) {
      totalResults += event.results.total_count;
    }
  });

  return {
    totalSearches,
    averageResults: totalSearches > 0 ? Math.round(totalResults / totalSearches) : 0,
    filterUsage,
    lastUpdated: new Date().toISOString(),
  };
}

function getEmptySummary() {
  return {
    totalSearches: 0,
    averageResults: 0,
    filterUsage: {},
    lastUpdated: new Date().toISOString(),
  };
}
